import { type DragEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  BellOff,
  BellRing,
  CalendarClock,
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  CreditCard,
  Crown,
  Download,
  Eye,
  EyeOff,
  FileSpreadsheet,
  FileText,
  GripVertical,
  LogOut,
  Mail,
  MessageSquare,
  Moon,
  Palette,
  Pin,
  PinOff,
  Plus,
  Save,
  Share2,
  Sun,
  Tags,
  Trash2,
  UserRound,
  Users,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../stores/authStore';
import { useSpaceStore } from '../stores/spaceStore';
import { useCategoryStore } from '../stores/categoryStore';
import { useThemeStore, type ThemeMode } from '../stores/themeStore';
import { usePushNotifications } from '../hooks/usePushNotifications';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import BottomSheet from '../components/ui/BottomSheet';
import Badge from '../components/ui/Badge';
import ProUpgradeModal from '../components/ProUpgradeModal';
import { CATEGORY_EMOJIS } from 'pairtab-shared/constants';

type ThemeChoice = ThemeMode;
type SpaceType = 'couple' | 'family' | 'roommates' | 'trip';
type MemberRole = 'owner' | 'member';
type CategoryAction = 'split' | 'keep';

type ProfileUser = {
  id: string;
  email: string;
  display_name?: string;
  avatar_emoji?: string;
  user_metadata?: {
    display_name?: string;
    avatar_emoji?: string;
  };
  pro_trial_ends_at?: string | null;
};

type Member = {
  id: string;
  user_id: string;
  display_name: string;
  avatar_emoji: string;
  role: MemberRole;
  joined_at: string;
};

type Space = {
  id: string;
  name: string;
  space_type: SpaceType;
  currency: string;
  start_date: string;
  invite_token: string;
  is_active: boolean;
};

type FixedConfig = {
  id?: string;
  category_id: string;
  amount_cents: number;
  due_day: number;
  autopay: boolean;
  is_paused: boolean;
  alternate_turn?: boolean;
  paid_by?: string;
};

type Category = {
  id: string;
  space_id: string;
  name: string;
  emoji: string;
  is_fixed: boolean;
  is_custom: boolean;
  is_pinned: boolean;
  is_hidden?: boolean;
  sort_order: number;
  fixed_configs?: FixedConfig[];
  category_defaults?: Array<{ action: CategoryAction; split_ratio: number }>;
};

type ProStatus = {
  is_pro: boolean;
  is_trial: boolean;
  trial_days_left: number;
  has_subscription: boolean;
};

const SPACE_TYPES: SpaceType[] = ['couple', 'family', 'roommates', 'trip'];
const CURRENCIES = ['CAD', 'USD', 'EUR', 'GBP'];

const NOTIFICATION_TYPES = [
  { key: 'expense_added', preferenceKey: 'notify_expense_added', label: 'Expense added', icon: '💸', description: 'When a new expense is logged' },
  { key: 'auto_log', preferenceKey: 'notify_fixed_autolog', label: 'Fixed auto-log', icon: '🔁', description: 'When a fixed expense logs itself' },
  { key: 'due_reminder', preferenceKey: 'notify_fixed_due', label: 'Due reminders', icon: '⏰', description: 'Before a fixed expense is due' },
  { key: 'settle_up', preferenceKey: 'notify_settle_up', label: 'Settle up', icon: '🤝', description: 'When someone records a settlement' },
  { key: 'budget_alert', preferenceKey: 'notify_budget_alert', label: 'Budget alerts', icon: '📊', description: 'When spending nears your cap' },
  { key: 'expense_edited', preferenceKey: 'notify_expense_edited', label: 'Edited expenses', icon: '✏️', description: 'When someone changes an expense' },
  { key: 'new_member', preferenceKey: 'notify_new_member', label: 'New members', icon: '👋', description: 'When someone joins the space' },
  { key: 'paused_fixed', preferenceKey: 'notify_fixed_paused', label: 'Paused fixed expenses', icon: '⏸️', description: 'When a fixed config is paused' },
  { key: 'inactive_partner', preferenceKey: 'notify_partner_inactive', label: 'Inactive partner', icon: '😴', description: 'When someone has not logged in a while' },
];

const currencyFormatter = (currency: string) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 2 });

function Section({
  title,
  icon: Icon,
  isOpen,
  onToggle,
  children,
}: {
  title: string;
  icon: typeof UserRound;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-3">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Icon className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="font-semibold">{title}</h2>
          </div>
        </div>
        {isOpen ? <ChevronUp className="w-4 h-4 text-muted" /> : <ChevronDown className="w-4 h-4 text-muted" />}
      </button>
      {isOpen && children}
    </div>
  );
}

export default function Settings() {
  const navigate = useNavigate();
  const { user, isPro, logout, setUser, updatePassword } = useAuthStore();
  const { theme, setTheme } = useThemeStore();
  const {
    currentSpace,
    members,
    fetchMembers,
    fetchSpace,
    setCurrentSpace,
  } = useSpaceStore();
  const {
    categories,
    fetchCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    updateCategoryDefault,
  } = useCategoryStore();
  const {
    permission: pushPermission,
    isSubscribed,
    isLoading: pushLoading,
    subscribe: subscribePush,
    unsubscribe: unsubscribePush,
  } = usePushNotifications();

  const typedUser = user as ProfileUser | null;
  const typedSpace = currentSpace as Space | null;
  const typedMembers = members as Member[];
  const typedCategories = categories as Category[];
  const pushEnabled = isSubscribed;
  const canTogglePush = pushPermission !== 'unsupported';
  const pushButtonLabel = pushEnabled ? 'Disable' : 'Enable';
  const pushDescription =
    pushPermission === 'denied'
      ? 'Blocked in browser'
      : pushPermission === 'unsupported'
      ? 'Unsupported in this browser'
      : pushEnabled
      ? 'Enabled on this device'
      : 'Turn on alerts for reminders and updates';

  const [showThemeSheet, setShowThemeSheet] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isExporting, setIsExporting] = useState<'csv' | 'pdf' | null>(null);
  const [preferences, setPreferences] = useState<Record<string, any>>({});
  const [savingPreferenceKey, setSavingPreferenceKey] = useState<string | null>(null);
  const [proStatus, setProStatus] = useState<ProStatus | null>(null);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    profile: true,
    space: true,
    categories: true,
    fixed: true,
    notifications: true,
    billing: true,
    exports: true,
    danger: true,
  });

  const [profileDraft, setProfileDraft] = useState({
    display_name: '',
    avatar_emoji: '👤',
    currency: 'CAD',
  });
  const [spaceDraft, setSpaceDraft] = useState({
    name: '',
    space_type: 'couple' as SpaceType,
    currency: 'CAD',
    start_date: '',
  });
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePhone, setInvitePhone] = useState('');
  const [inviteCopied, setInviteCopied] = useState(false);
  const [budgetDrafts, setBudgetDrafts] = useState<Record<string, string>>({});
  const [categoryOrder, setCategoryOrder] = useState<string[]>([]);
  const [draggingCategoryId, setDraggingCategoryId] = useState<string | null>(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryEmoji, setNewCategoryEmoji] = useState('📦');
  const [newCategoryFixed, setNewCategoryFixed] = useState(false);
  const [newCategoryAction, setNewCategoryAction] = useState<CategoryAction>('split');
  const [newCategorySplitRatio, setNewCategorySplitRatio] = useState(50);
  const [spaceDeleteConfirm, setSpaceDeleteConfirm] = useState('');
  const [accountDeleteConfirm, setAccountDeleteConfirm] = useState('');
  const [passwordDraft, setPasswordDraft] = useState({
    next: '',
    confirm: '',
  });
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  const myMember = useMemo(
    () => typedMembers.find((member) => member.user_id === typedUser?.id) ?? typedMembers[0] ?? null,
    [typedMembers, typedUser?.id]
  );
  const isOwner = myMember?.role === 'owner';
  const currentCurrency = spaceDraft.currency || profileDraft.currency || 'CAD';
  const money = currencyFormatter(currentCurrency);

  const orderedCategories = useMemo(() => {
    const byId = new Map(typedCategories.map((category) => [category.id, category]));
    const hydrated = categoryOrder
      .map((id) => byId.get(id))
      .filter(Boolean) as Category[];
    const leftovers = typedCategories.filter((category) => !categoryOrder.includes(category.id));
    return [...hydrated, ...leftovers].sort((a, b) => {
      const aPinned = a.is_pinned ? 0 : 1;
      const bPinned = b.is_pinned ? 0 : 1;
      if (aPinned !== bPinned) return aPinned - bPinned;
      return (a.sort_order ?? 0) - (b.sort_order ?? 0);
    });
  }, [typedCategories, categoryOrder]);

  const visibleFixedCategories = orderedCategories.filter((category) => category.is_fixed);
  const suggestedInviteeName = useMemo(
    () =>
      typedMembers.find((member) => member.role === 'member' && member.display_name.trim())?.display_name?.trim() || '',
    [typedMembers]
  );
  const inviteUrl = useMemo(() => {
    if (!typedSpace?.invite_token) return '';

    const baseUrl = `${window.location.origin}/onboarding/join/${typedSpace.invite_token}`;
    const searchParams = new URLSearchParams();

    if (suggestedInviteeName) {
      searchParams.set('prefillName', suggestedInviteeName);
    }

    return searchParams.toString() ? `${baseUrl}?${searchParams.toString()}` : baseUrl;
  }, [typedSpace?.invite_token, suggestedInviteeName]);

  useEffect(() => {
    setProfileDraft({
      display_name: typedUser?.display_name || typedUser?.user_metadata?.display_name || '',
      avatar_emoji: typedUser?.avatar_emoji || typedUser?.user_metadata?.avatar_emoji || '👤',
      currency: typedSpace?.currency || 'CAD',
    });
  }, [typedUser?.display_name, typedUser?.avatar_emoji, typedUser?.user_metadata?.display_name, typedUser?.user_metadata?.avatar_emoji, typedSpace?.currency]);

  useEffect(() => {
    setSpaceDraft({
      name: typedSpace?.name || '',
      space_type: typedSpace?.space_type || 'couple',
      currency: typedSpace?.currency || 'CAD',
      start_date: typedSpace?.start_date || new Date().toISOString().split('T')[0],
    });
  }, [typedSpace?.name, typedSpace?.space_type, typedSpace?.currency, typedSpace?.start_date]);

  useEffect(() => {
    if (typedSpace?.id) {
      fetchMembers(typedSpace.id);
      fetchCategories(typedSpace.id);
    }
  }, [typedSpace?.id, fetchMembers, fetchCategories]);

  useEffect(() => {
    setCategoryOrder(typedCategories.map((category) => category.id));
  }, [typedCategories]);

  useEffect(() => {
    if (!typedUser?.id || !typedSpace?.id) return;

    const key = `pairtab:budgets:${typedUser.id}:${typedSpace.id}`;
    try {
      setBudgetDrafts(JSON.parse(localStorage.getItem(key) || '{}'));
    } catch {
      setBudgetDrafts({});
    }
  }, [typedUser?.id, typedSpace?.id]);

  useEffect(() => {
    if (!typedUser) return;

    const fetchPreferences = async () => {
      const { data } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', typedUser.id)
        .single();

      if (data) {
        setPreferences(data);
      }
    };

    fetchPreferences();
  }, [typedUser?.id]);

  useEffect(() => {
    if (!typedUser) return;

    const fetchProStatus = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch('/api/stripe/status', {
          headers: { Authorization: `Bearer ${session?.access_token}` },
        });
        if (res.ok) {
          setProStatus(await res.json());
        }
      } catch {
        // ignore
      }
    };

    fetchProStatus();
  }, [typedUser?.id]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
      toast.success('Welcome to PairTab Pro!');
      window.history.replaceState({}, '', '/settings');
    }
    if (params.get('canceled') === 'true') {
      toast('Upgrade canceled', { icon: 'ℹ️' });
      window.history.replaceState({}, '', '/settings');
    }
  }, []);

  const toggleSection = (key: string) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const updatePreference = async (key: string, value: any) => {
    if (!typedUser?.id) return false;

    setSavingPreferenceKey(key);

    const { data, error } = await supabase
      .from('user_preferences')
      .upsert(
        { user_id: typedUser.id, [key]: value },
        { onConflict: 'user_id' }
      )
      .select('*')
      .single();

    setSavingPreferenceKey(null);

    if (error) {
      console.error(`Failed to update preference "${key}"`, error);
      toast.error('Could not update notification setting.');
      return false;
    }

    if (data) {
      setPreferences(data);
    } else {
      setPreferences((prev) => ({ ...prev, [key]: value }));
    }

    return true;
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      navigate('/login');
    } catch {
      toast.error('Failed to log out');
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handlePushToggle = async () => {
    if (!canTogglePush) {
      toast.error('Push notifications are not supported in this browser.');
      return;
    }

    if (pushEnabled) {
      const disabled = await unsubscribePush();
      if (disabled) {
        toast.success('Push notifications disabled');
      } else {
        toast.error('Could not disable push notifications.');
      }
      return;
    }

    const enabled = await subscribePush();
    if (typeof Notification !== 'undefined' && Notification.permission === 'denied') {
      toast.error('Notifications are blocked in this browser.');
    } else if (enabled) {
      toast.success('Push notifications enabled');
    } else {
      toast.error('Could not enable push notifications.');
    }
  };

  const handleExport = async (format: 'csv' | 'pdf') => {
    if (!isPro) {
      setShowUpgradeModal(true);
      return;
    }
    if (!typedSpace?.id) {
      toast.error('No active space selected');
      return;
    }

    setIsExporting(format);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`/api/export/${format}/${typedSpace.id}`, {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });

      if (!res.ok) {
        const error = await res.json().catch(() => ({ error: 'Export failed' }));
        throw new Error(error.error);
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = format === 'csv' ? 'pairtab-export.csv' : 'pairtab-report.pdf';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (err: any) {
      toast.error(err.message || 'Export failed');
    } finally {
      setIsExporting(null);
    }
  };

  const saveProfile = async () => {
    if (!typedUser?.id) return;

    try {
      await supabase
        .from('users')
        .update({
          display_name: profileDraft.display_name.trim(),
          avatar_emoji: profileDraft.avatar_emoji,
        })
        .eq('id', typedUser.id);

      if (typedSpace?.id) {
        await supabase
          .from('space_members')
          .update({
            display_name: profileDraft.display_name.trim(),
            avatar_emoji: profileDraft.avatar_emoji,
          })
          .eq('space_id', typedSpace.id)
          .eq('user_id', typedUser.id);
        await fetchMembers(typedSpace.id);
      }

      setUser({
        ...(typedUser as any),
        display_name: profileDraft.display_name.trim(),
        avatar_emoji: profileDraft.avatar_emoji,
        user_metadata: {
          ...(typedUser?.user_metadata || {}),
          display_name: profileDraft.display_name.trim(),
          avatar_emoji: profileDraft.avatar_emoji,
        },
      });

      toast.success('Profile updated');
    } catch {
      toast.error('Failed to update profile');
    }
  };

  const saveBudgets = () => {
    if (!typedUser?.id || !typedSpace?.id) return;

    localStorage.setItem(
      `pairtab:budgets:${typedUser.id}:${typedSpace.id}`,
      JSON.stringify(budgetDrafts)
    );
    toast.success('Budgets saved');
  };

  const changePassword = async () => {
    if (!passwordDraft.next.trim()) {
      toast.error('Enter a new password');
      return;
    }

    if (passwordDraft.next.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (passwordDraft.next !== passwordDraft.confirm) {
      toast.error('Passwords do not match');
      return;
    }

    setIsUpdatingPassword(true);

    try {
      await updatePassword(passwordDraft.next);
      setPasswordDraft({ next: '', confirm: '' });
      toast.success('Password updated');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update password');
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const saveSpace = async () => {
    if (!typedSpace?.id) return;

    try {
      await supabase
        .from('spaces')
        .update(spaceDraft)
        .eq('id', typedSpace.id);

      await fetchSpace(typedSpace.id);
      toast.success('Space updated');
    } catch {
      toast.error('Failed to update space');
    }
  };

  const sendInvite = async (type: 'email' | 'sms') => {
    if (!typedSpace) return;

    const body = type === 'email'
      ? {
          space_id: typedSpace.id,
          email: inviteEmail.trim(),
          invitee_name: inviteEmail.trim().split('@')[0] || 'Partner',
        }
      : {
          space_id: typedSpace.id,
          phone: invitePhone.trim(),
          invitee_name: invitePhone.trim(),
        };

    if (type === 'email' && !inviteEmail.trim()) {
      toast.error('Enter an email');
      return;
    }
    if (type === 'sms' && !invitePhone.trim()) {
      toast.error('Enter a phone number');
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`/api/invites/${type}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      const payload = await res.json().catch(() => ({}));

      if (!res.ok) {
        if (!payload.mock) throw new Error(payload.error || `Failed to send ${type}`);
      }

      toast.success(
        payload.mock
          ? `Demo mode only: no real ${type === 'email' ? 'email' : 'SMS'} was delivered`
          : `${type === 'email' ? 'Email' : 'SMS'} invite sent`
      );
      if (type === 'email') setInviteEmail('');
      if (type === 'sms') setInvitePhone('');
    } catch (err: any) {
      toast.error(err.message || `Failed to send ${type}`);
    }
  };

  const copyInviteLink = async () => {
    if (!inviteUrl) {
      toast.error('Invite link is not ready yet');
      return;
    }

    try {
      await navigator.clipboard.writeText(inviteUrl);
      setInviteCopied(true);
      window.setTimeout(() => setInviteCopied(false), 2000);
      toast.success('Invite link copied');
    } catch {
      toast.error('Failed to copy invite link');
    }
  };

  const shareInviteLink = async () => {
    if (!inviteUrl) {
      toast.error('Invite link is not ready yet');
      return;
    }

    try {
      const shareText = `Join my ${typedSpace?.name || 'PairTab'} space on PairTab to track expenses together!`;
      if (navigator.share) {
        await navigator.share({ title: 'Join PairTab', text: shareText, url: inviteUrl });
      } else {
        await copyInviteLink();
      }
    } catch {
      // User cancelled or share failed silently.
    }
  };

  const removeMember = async (memberId: string) => {
    if (!typedSpace?.id || !isOwner) return;
    try {
      await supabase
        .from('space_members')
        .update({ left_at: new Date().toISOString() })
        .eq('id', memberId);
      await fetchMembers(typedSpace.id);
      toast.success('Member removed');
    } catch {
      toast.error('Failed to remove member');
    }
  };

  const transferOwnership = async (memberId: string) => {
    if (!typedSpace?.id || !myMember || !isOwner) return;
    try {
      await supabase.from('space_members').update({ role: 'member' }).eq('id', myMember.id);
      await supabase.from('space_members').update({ role: 'owner' }).eq('id', memberId);
      await fetchMembers(typedSpace.id);
      toast.success('Ownership transferred');
    } catch {
      toast.error('Failed to transfer ownership');
    }
  };

  const leaveSpace = async () => {
    if (!typedSpace?.id || !myMember) return;
    try {
      await supabase
        .from('space_members')
        .update({ left_at: new Date().toISOString() })
        .eq('id', myMember.id);
      setCurrentSpace(null);
      toast.success('You left the space');
      navigate('/');
    } catch {
      toast.error('Failed to leave space');
    }
  };

  const deleteSpace = async () => {
    if (!typedSpace?.id || !isOwner) return;
    if (spaceDeleteConfirm !== typedSpace.name) {
      toast.error('Type the space name to confirm');
      return;
    }

    try {
      await supabase
        .from('spaces')
        .update({ is_active: false })
        .eq('id', typedSpace.id);
      setCurrentSpace(null);
      toast.success('Space archived');
      navigate('/');
    } catch {
      toast.error('Failed to archive space');
    }
  };

  const deleteAccount = async () => {
    if (!typedUser?.email || accountDeleteConfirm !== typedUser.email) {
      toast.error('Type your email to confirm');
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch('/api/auth/account', {
        method: 'DELETE',
        headers: {
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to delete account');
      }

      await logout().catch(() => undefined);
      toast.success('Account deleted');
      navigate('/login');
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete account');
    }
  };

  const updateCategoryOrder = async (nextOrder: string[]) => {
    if (!typedSpace?.id) return;
    setCategoryOrder(nextOrder);

    try {
      await Promise.all(
        nextOrder.map((id, index) =>
          supabase.from('categories').update({ sort_order: index }).eq('id', id)
        )
      );
      await fetchCategories(typedSpace.id);
    } catch {
      toast.error('Failed to save category order');
    }
  };

  const handleCategoryDrop = async (targetCategoryId: string) => {
    if (!draggingCategoryId || draggingCategoryId === targetCategoryId) return;

    const nextOrder = [...categoryOrder];
    const fromIndex = nextOrder.indexOf(draggingCategoryId);
    const toIndex = nextOrder.indexOf(targetCategoryId);
    if (fromIndex === -1 || toIndex === -1) return;
    nextOrder.splice(fromIndex, 1);
    nextOrder.splice(toIndex, 0, draggingCategoryId);
    setDraggingCategoryId(null);
    await updateCategoryOrder(nextOrder);
  };

  const saveCategory = async (category: Category, updates: Partial<Category>, defaultUpdate?: { action: CategoryAction; split_ratio: number }) => {
    try {
      await updateCategory(category.id, updates as any);
      if (defaultUpdate && typedSpace?.id) {
        await updateCategoryDefault(category.id, typedSpace.id, defaultUpdate.action, defaultUpdate.split_ratio);
      }
      if (typedSpace?.id) await fetchCategories(typedSpace.id);
      toast.success('Category updated');
    } catch {
      toast.error('Failed to update category');
    }
  };

  const addCategory = async () => {
    if (!typedSpace?.id || !newCategoryName.trim()) {
      toast.error('Enter a category name');
      return;
    }

    try {
      const created = await createCategory({
        space_id: typedSpace.id,
        name: newCategoryName.trim(),
        emoji: newCategoryEmoji,
        is_fixed: newCategoryFixed,
        is_custom: true,
        is_pinned: false,
        is_hidden: false,
        sort_order: typedCategories.length,
      });

      await updateCategoryDefault(created.id, typedSpace.id, newCategoryAction, newCategorySplitRatio);
      await fetchCategories(typedSpace.id);
      setNewCategoryName('');
      setNewCategoryEmoji('📦');
      setNewCategoryFixed(false);
      setNewCategoryAction('split');
      setNewCategorySplitRatio(50);
      toast.success('Category added');
    } catch (err: any) {
      toast.error(err.message || 'Failed to add category');
    }
  };

  const saveFixedConfig = async (category: Category, config: FixedConfig) => {
    if (!typedSpace?.id) return;

    try {
      if (config.id) {
        await supabase
          .from('fixed_configs')
          .update({
            amount_cents: config.amount_cents,
            due_day: config.due_day,
            autopay: config.autopay,
            is_paused: config.is_paused,
            paid_by: config.paid_by || null,
          })
          .eq('id', config.id);
      } else {
        await supabase.from('fixed_configs').insert({
          category_id: category.id,
          space_id: typedSpace.id,
          amount_cents: config.amount_cents,
          due_day: config.due_day,
          autopay: config.autopay,
          is_paused: config.is_paused,
          paid_by: config.paid_by || null,
        });
      }

      await fetchCategories(typedSpace.id);
      toast.success('Fixed config updated');
    } catch {
      toast.error('Failed to save fixed config');
    }
  };

  const upgradeToPro = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/stripe/create-checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ plan: 'monthly' }),
      });

      const payload = await res.json();
      if (!payload.url) throw new Error(payload.error || 'Checkout failed');
      window.location.href = payload.url;
    } catch (err: any) {
      toast.error(err.message || 'Failed to start checkout');
    }
  };

  const displayName = profileDraft.display_name || typedUser?.user_metadata?.display_name || 'User';
  const avatar = profileDraft.avatar_emoji || typedUser?.user_metadata?.avatar_emoji || '👤';

  return (
    <div className="space-y-6 pb-8">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/')}
          className="p-2 -ml-2 rounded-full hover:bg-muted/10 touch-target"
        >
          <ArrowLeft size={24} />
        </button>
        <h1 className="text-xl font-bold">Settings</h1>
      </div>

      <Card className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center text-3xl">
          {avatar}
        </div>
        <div className="flex-1">
          <h2 className="font-semibold text-lg">{displayName}</h2>
          <p className="text-muted text-sm">{typedUser?.email}</p>
          <div className="flex items-center gap-2 mt-2">
            {isPro ? (
              <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                <Crown className="w-3 h-3" /> PRO
              </span>
            ) : proStatus?.is_trial ? (
              <span className="text-xs bg-yellow-400/20 text-yellow-400 px-2 py-0.5 rounded-full font-medium">
                Trial · {proStatus.trial_days_left}d left
              </span>
            ) : (
              <span className="text-xs bg-muted/30 text-muted px-2 py-0.5 rounded-full font-medium">
                Free
              </span>
            )}
          </div>
        </div>
      </Card>

      <Section title="Profile" icon={UserRound} isOpen={openSections.profile} onToggle={() => toggleSection('profile')}>
        <div className="space-y-3">
          <Card className="space-y-4">
            <Input
              label="Display Name"
              value={profileDraft.display_name}
              onChange={(event) => setProfileDraft((prev) => ({ ...prev, display_name: event.target.value }))}
              placeholder="Your name"
            />
            <div className="space-y-2">
              <p className="text-sm font-medium">Avatar Emoji</p>
              <div className="grid grid-cols-8 gap-2">
                {CATEGORY_EMOJIS.slice(0, 24).map((emoji) => (
                  <button
                    key={emoji}
                    onClick={() => setProfileDraft((prev) => ({ ...prev, avatar_emoji: emoji }))}
                    className={`h-10 rounded-xl text-xl ${profileDraft.avatar_emoji === emoji ? 'bg-primary/20 ring-2 ring-primary' : 'bg-muted/10'}`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">Preferred Currency</p>
              <div className="grid grid-cols-4 gap-2">
                {CURRENCIES.map((currency) => (
                  <button
                    key={currency}
                    onClick={() => setProfileDraft((prev) => ({ ...prev, currency }))}
                    className={`rounded-xl border px-3 py-2 text-sm font-medium ${profileDraft.currency === currency ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-card'}`}
                  >
                    {currency}
                  </button>
                ))}
              </div>
            </div>
            <Button onClick={saveProfile} className="w-full">
              <Save className="w-4 h-4" />
              Save Profile
            </Button>
          </Card>

          <Card className="space-y-3">
            <div>
              <h3 className="font-medium">Monthly Budgets</h3>
              <p className="text-xs text-muted">Optional, saved per category on this device.</p>
            </div>
            {orderedCategories.map((category) => (
              <div key={category.id} className="grid grid-cols-[1fr_auto] gap-3 items-center">
                <div className="flex items-center gap-2">
                  <span className="text-xl">{category.emoji}</span>
                  <span className="text-sm font-medium">{category.name}</span>
                </div>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={budgetDrafts[category.id] || ''}
                  onChange={(event) => setBudgetDrafts((prev) => ({ ...prev, [category.id]: event.target.value }))}
                  placeholder="0.00"
                  className="input-field w-28"
                />
              </div>
            ))}
            <Button variant="secondary" onClick={saveBudgets} className="w-full">
              Save Budgets
            </Button>
          </Card>

          <Card className="space-y-4">
            <div>
              <h3 className="font-medium">Change Password</h3>
              <p className="text-xs text-muted">Update the password for your signed-in account.</p>
            </div>
            <Input
              label="New Password"
              type="password"
              value={passwordDraft.next}
              onChange={(event) => setPasswordDraft((prev) => ({ ...prev, next: event.target.value }))}
              placeholder="At least 8 characters"
              autoComplete="new-password"
            />
            <Input
              label="Confirm New Password"
              type="password"
              value={passwordDraft.confirm}
              onChange={(event) => setPasswordDraft((prev) => ({ ...prev, confirm: event.target.value }))}
              placeholder="Re-enter your new password"
              autoComplete="new-password"
            />
            <Button onClick={changePassword} className="w-full" disabled={isUpdatingPassword}>
              {isUpdatingPassword ? 'Updating...' : 'Change Password'}
            </Button>
          </Card>

          <Card className="flex items-center justify-between py-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Palette className="w-5 h-5 text-primary" />
              </div>
              <div>
                <span className="font-medium">Theme</span>
                <p className="text-xs text-muted capitalize">{theme}</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setShowThemeSheet(true)}>
              Change
            </Button>
          </Card>
        </div>
      </Section>

      <Section title="Space Management" icon={Users} isOpen={openSections.space} onToggle={() => toggleSection('space')}>
        <div className="space-y-3">
          <Card className="space-y-4">
            <Input
              label="Space Name"
              value={spaceDraft.name}
              onChange={(event) => setSpaceDraft((prev) => ({ ...prev, name: event.target.value }))}
            />
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <p className="text-sm font-medium">Space Type</p>
                <select
                  value={spaceDraft.space_type}
                  onChange={(event) => setSpaceDraft((prev) => ({ ...prev, space_type: event.target.value as SpaceType }))}
                  className="input-field"
                >
                  {SPACE_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type[0].toUpperCase() + type.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium">Currency</p>
                <select
                  value={spaceDraft.currency}
                  onChange={(event) => setSpaceDraft((prev) => ({ ...prev, currency: event.target.value }))}
                  className="input-field"
                >
                  {CURRENCIES.map((currency) => (
                    <option key={currency} value={currency}>
                      {currency}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <Input
              label="Start Date"
              type="date"
              value={spaceDraft.start_date}
              onChange={(event) => setSpaceDraft((prev) => ({ ...prev, start_date: event.target.value }))}
            />
            <Button onClick={saveSpace} className="w-full">
              <Save className="w-4 h-4" />
              Save Space
            </Button>
          </Card>

          <Card className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium">Members</h3>
                <p className="text-xs text-muted">{typedMembers.length} active members</p>
              </div>
              {typedSpace?.invite_token && (
                <Badge variant="fixed" size="sm">Invite Ready</Badge>
              )}
            </div>
            {typedMembers.map((member) => (
              <div key={member.id} className="rounded-2xl border border-border p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-xl">
                      {member.avatar_emoji}
                    </div>
                    <div>
                      <p className="font-medium">{member.display_name}</p>
                      <p className="text-xs text-muted">{member.role}</p>
                    </div>
                  </div>
                  {member.role === 'owner' && <Badge variant="split" size="sm">Owner</Badge>}
                </div>
                {isOwner && member.id !== myMember?.id && (
                  <div className="flex gap-2">
                    <Button variant="secondary" size="sm" onClick={() => transferOwnership(member.id)} className="flex-1">
                      Transfer Ownership
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => removeMember(member.id)} className="flex-1">
                      Remove
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </Card>

          <Card className="space-y-3">
            <div>
              <h3 className="font-medium">Invite Member</h3>
              <p className="text-xs text-muted">Reuse the onboarding invite flow from settings.</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Invite Link</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  readOnly
                  value={inviteUrl}
                  className="input-field text-sm"
                  placeholder="Invite link will appear here"
                />
                <Button variant="primary" onClick={copyInviteLink} disabled={!inviteUrl} className="shrink-0 px-4">
                  {inviteCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {inviteCopied ? 'Copied' : 'Copy'}
                </Button>
              </div>
              <Button variant="secondary" onClick={shareInviteLink} disabled={!inviteUrl} className="w-full">
                <Share2 className="w-4 h-4" />
                Share Invite Link
              </Button>
            </div>
            <Input
              label="Email Invite"
              type="email"
              value={inviteEmail}
              onChange={(event) => setInviteEmail(event.target.value)}
              placeholder="friend@example.com"
            />
            <Button variant="secondary" onClick={() => sendInvite('email')} className="w-full">
              <Mail className="w-4 h-4" />
              Send Email Invite
            </Button>
            <Input
              label="SMS Invite"
              type="tel"
              value={invitePhone}
              onChange={(event) => setInvitePhone(event.target.value)}
              placeholder="+1 555 555 5555"
            />
            <Button variant="secondary" onClick={() => sendInvite('sms')} className="w-full">
              <MessageSquare className="w-4 h-4" />
              Send SMS Invite
            </Button>
          </Card>
        </div>
      </Section>

      <Section title="Category Management" icon={Tags} isOpen={openSections.categories} onToggle={() => toggleSection('categories')}>
        <div className="space-y-3">
          <Card className="space-y-3">
            <div>
              <h3 className="font-medium">Add Custom Category</h3>
              <p className="text-xs text-muted">New categories inherit your chosen split default.</p>
            </div>
            <div className="grid grid-cols-[auto_1fr] gap-3 items-center">
              <button
                onClick={() => {
                  const current = CATEGORY_EMOJIS.indexOf(newCategoryEmoji);
                  const next = CATEGORY_EMOJIS[(current + 1) % CATEGORY_EMOJIS.length];
                  setNewCategoryEmoji(next);
                }}
                className="w-12 h-12 rounded-2xl bg-primary/10 text-2xl"
              >
                {newCategoryEmoji}
              </button>
              <Input
                label="Name"
                value={newCategoryName}
                onChange={(event) => setNewCategoryName(event.target.value)}
                placeholder="Gym, Pets, Internet"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button variant={newCategoryFixed ? 'secondary' : 'primary'} onClick={() => setNewCategoryFixed(false)}>
                Variable
              </Button>
              <Button variant={newCategoryFixed ? 'primary' : 'secondary'} onClick={() => setNewCategoryFixed(true)}>
                Fixed
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button variant={newCategoryAction === 'split' ? 'primary' : 'secondary'} onClick={() => setNewCategoryAction('split')}>
                Split
              </Button>
              <Button variant={newCategoryAction === 'keep' ? 'primary' : 'secondary'} onClick={() => setNewCategoryAction('keep')}>
                Keep
              </Button>
            </div>
            {newCategoryAction === 'split' && (
              <Input
                label="Default Split Ratio"
                type="number"
                min="0"
                max="100"
                value={String(newCategorySplitRatio)}
                onChange={(event) => setNewCategorySplitRatio(Number(event.target.value || 50))}
              />
            )}
            <Button onClick={addCategory} className="w-full">
              <Plus className="w-4 h-4" />
              Add Category
            </Button>
          </Card>

          {orderedCategories.map((category) => {
            const defaultPref = category.category_defaults?.[0] || { action: 'split' as const, split_ratio: 50 };
            return (
              <div
                key={category.id}
                draggable
                onDragStart={() => setDraggingCategoryId(category.id)}
                onDragOver={(event: DragEvent<HTMLDivElement>) => event.preventDefault()}
                onDrop={() => handleCategoryDrop(category.id)}
              >
              <Card className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 flex-1">
                    <GripVertical className="w-4 h-4 text-muted mt-3" />
                    <button
                      onClick={() => saveCategory(category, { emoji: CATEGORY_EMOJIS[(CATEGORY_EMOJIS.indexOf(category.emoji) + 1) % CATEGORY_EMOJIS.length] })}
                      className="w-11 h-11 rounded-2xl bg-primary/10 text-2xl"
                    >
                      {category.emoji}
                    </button>
                    <div className="flex-1">
                      <Input
                        label="Category Name"
                        value={category.name}
                        onChange={(event) => {
                          const next = orderedCategories.map((item) =>
                            item.id === category.id ? { ...item, name: event.target.value } : item
                          );
                          setCategoryOrder(next.map((item) => item.id));
                          void saveCategory(category, { name: event.target.value });
                        }}
                      />
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => saveCategory(category, { is_pinned: !category.is_pinned })}
                      className="p-2 rounded-xl bg-muted/10"
                      aria-label="Toggle pin"
                    >
                      {category.is_pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => saveCategory(category, { is_hidden: !category.is_hidden })}
                      className="p-2 rounded-xl bg-muted/10"
                      aria-label="Toggle visibility"
                    >
                      {category.is_hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted">Default Action</p>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        size="sm"
                        variant={defaultPref.action === 'split' ? 'primary' : 'secondary'}
                        onClick={() => saveCategory(category, {}, { action: 'split', split_ratio: defaultPref.split_ratio })}
                      >
                        Split
                      </Button>
                      <Button
                        size="sm"
                        variant={defaultPref.action === 'keep' ? 'primary' : 'secondary'}
                        onClick={() => saveCategory(category, {}, { action: 'keep', split_ratio: defaultPref.split_ratio })}
                      >
                        Keep
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted">Split Ratio</p>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={defaultPref.split_ratio}
                      onChange={(event) =>
                        saveCategory(category, {}, {
                          action: defaultPref.action,
                          split_ratio: Number(event.target.value || 50),
                        })
                      }
                      className="input-field"
                      disabled={defaultPref.action === 'keep'}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs text-muted">
                  <div className="flex items-center gap-2">
                    {category.is_hidden ? <Badge variant="keep" size="sm">Hidden</Badge> : null}
                    {category.is_pinned ? <Badge variant="split" size="sm">Pinned</Badge> : null}
                    {category.is_fixed ? <Badge variant="fixed" size="sm">Fixed</Badge> : null}
                  </div>
                  {category.is_custom && isOwner && (
                    <Button variant="danger" size="sm" onClick={() => deleteCategory(category.id)}>
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </Button>
                  )}
                </div>
              </Card>
              </div>
            );
          })}
        </div>
      </Section>

      <Section title="Fixed Config Management" icon={CalendarClock} isOpen={openSections.fixed} onToggle={() => toggleSection('fixed')}>
        <div className="space-y-3">
          {visibleFixedCategories.length === 0 ? (
            <Card>
              <p className="text-sm text-muted">No fixed categories yet.</p>
            </Card>
          ) : (
            visibleFixedCategories.map((category) => {
              const config = category.fixed_configs?.[0] || {
                category_id: category.id,
                amount_cents: 0,
                due_day: 1,
                autopay: false,
                is_paused: false,
                paid_by: myMember?.id,
              };

              return (
                <Card key={category.id} className="space-y-4">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{category.emoji}</span>
                    <div className="flex-1">
                      <h3 className="font-medium">{category.name}</h3>
                      <p className="text-xs text-muted">
                        {config.amount_cents ? money.format(config.amount_cents / 100) : 'Not configured yet'}
                      </p>
                    </div>
                    <Badge variant="fixed" size="sm">Fixed</Badge>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label="Amount"
                      type="number"
                      min="0"
                      step="0.01"
                      value={String(config.amount_cents / 100)}
                      onChange={(event) => {
                        const next = {
                          ...config,
                          amount_cents: Math.round(Number(event.target.value || 0) * 100),
                        };
                        void saveFixedConfig(category, next);
                      }}
                    />
                    <Input
                      label="Due Day"
                      type="number"
                      min="1"
                      max="31"
                      value={String(config.due_day)}
                      onChange={(event) => {
                        const next = {
                          ...config,
                          due_day: Number(event.target.value || 1),
                        };
                        void saveFixedConfig(category, next);
                      }}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant={config.autopay ? 'primary' : 'secondary'}
                      onClick={() => saveFixedConfig(category, { ...config, autopay: !config.autopay })}
                    >
                      {config.autopay ? 'Autopay On' : 'Autopay Off'}
                    </Button>
                    <Button
                      variant={config.is_paused ? 'danger' : 'secondary'}
                      onClick={() => saveFixedConfig(category, { ...config, is_paused: !config.is_paused })}
                    >
                      {config.is_paused ? 'Paused' : 'Active'}
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm font-medium">Who Pays</p>
                    <div className="grid grid-cols-2 gap-2">
                      {typedMembers.map((member) => (
                        <Button
                          key={member.id}
                          size="sm"
                          variant={config.paid_by === member.id ? 'primary' : 'secondary'}
                          onClick={() => saveFixedConfig(category, { ...config, paid_by: member.id })}
                        >
                          {member.display_name}
                        </Button>
                      ))}
                    </div>
                  </div>
                </Card>
              );
            })
          )}
        </div>
      </Section>

      <Section title="Notifications" icon={BellRing} isOpen={openSections.notifications} onToggle={() => toggleSection('notifications')}>
        <div className="space-y-3">
          <Card className="flex items-center justify-between py-3">
            <div className="flex items-center gap-3">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  pushEnabled ? 'bg-primary/10' : 'bg-card-hover'
                }`}
              >
                {pushEnabled ? (
                  <BellRing className="w-5 h-5 text-primary" />
                ) : (
                  <BellOff className="w-5 h-5 text-muted" />
                )}
              </div>
              <div>
                <span className="font-medium">Push Notifications</span>
                <p className="text-xs text-muted">{pushDescription}</p>
              </div>
            </div>
            <Button
              variant={pushEnabled ? 'secondary' : 'primary'}
              size="sm"
              onClick={handlePushToggle}
              disabled={pushLoading || !canTogglePush}
            >
              {pushButtonLabel}
            </Button>
          </Card>

          {pushPermission === 'denied' && (
            <Card className="border-danger/30 py-3">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-danger" />
                <div>
                  <p className="text-sm font-medium text-danger">Notifications Blocked</p>
                  <p className="text-xs text-muted">Re-enable notifications in your browser settings for this site.</p>
                </div>
              </div>
            </Card>
          )}

          {NOTIFICATION_TYPES.map((type) => {
            const isProOnly = type.key === 'budget_alert';
            const enabled = preferences[type.preferenceKey] !== false;
            return (
              <Card key={type.key} className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-lg">
                    {type.icon}
                  </div>
                  <div>
                    <span className="font-medium">{type.label}</span>
                    <p className="text-xs text-muted">{type.description}</p>
                  </div>
                </div>
                <Button
                  variant={enabled ? 'primary' : 'secondary'}
                  size="sm"
                  disabled={savingPreferenceKey === type.preferenceKey}
                  onClick={() => {
                    if (isProOnly && !isPro) {
                      setShowUpgradeModal(true);
                      return;
                    }
                    void updatePreference(type.preferenceKey, !enabled);
                  }}
                >
                  {enabled ? 'On' : 'Off'}
                </Button>
              </Card>
            );
          })}

          <Card className="flex items-center justify-between py-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Mail className="w-5 h-5 text-primary" />
              </div>
              <div>
                <span className="font-medium">Email Digests</span>
                <p className="text-xs text-muted">{isPro ? 'Weekly and monthly reports' : 'Pro feature'}</p>
              </div>
            </div>
            <Button
              variant={preferences.email_digest_enabled !== false ? 'primary' : 'secondary'}
              size="sm"
              disabled={savingPreferenceKey === 'email_digest_enabled'}
              onClick={() => {
                if (!isPro) {
                  setShowUpgradeModal(true);
                  return;
                }
                void updatePreference('email_digest_enabled', preferences.email_digest_enabled === false);
              }}
            >
              {preferences.email_digest_enabled !== false ? 'On' : 'Off'}
            </Button>
          </Card>
        </div>
      </Section>

      <Section title="Billing" icon={CreditCard} isOpen={openSections.billing} onToggle={() => toggleSection('billing')}>
        <div className="space-y-3">
          <Card className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium">Current Plan</h3>
                <p className="text-xs text-muted">
                  {isPro ? 'PairTab Pro is active' : proStatus?.is_trial ? `Trial ends in ${proStatus.trial_days_left} days` : 'You are on the free plan'}
                </p>
              </div>
              {isPro ? <Badge variant="split" size="sm">PRO</Badge> : <Badge variant="keep" size="sm">FREE</Badge>}
            </div>
            {!isPro && (
              <Button onClick={upgradeToPro} className="w-full">
                <Crown className="w-4 h-4" />
                Upgrade to Pro
              </Button>
            )}
            {isPro && (
              <Button
                variant="secondary"
                onClick={async () => {
                  try {
                    const { data: { session } } = await supabase.auth.getSession();
                    const res = await fetch('/api/stripe/portal', {
                      headers: { Authorization: `Bearer ${session?.access_token}` },
                    });
                    const payload = await res.json();
                    if (!payload.url) throw new Error(payload.error || 'Portal unavailable');
                    window.location.href = payload.url;
                  } catch (err: any) {
                    toast.error(err.message || 'Failed to open billing portal');
                  }
                }}
                className="w-full"
              >
                Manage Subscription
              </Button>
            )}
          </Card>
        </div>
      </Section>

      <Section title="Exports" icon={Download} isOpen={openSections.exports} onToggle={() => toggleSection('exports')}>
        <div className="space-y-3">
          <Card interactive onClick={() => handleExport('csv')} className="flex items-center justify-between py-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <FileSpreadsheet className="w-5 h-5 text-primary" />
              </div>
              <div>
                <span className="font-medium">Export CSV</span>
                <p className="text-xs text-muted">All expenses and settlements</p>
              </div>
            </div>
            {isExporting === 'csv' ? 'Working...' : !isPro ? <Crown className="w-4 h-4 text-yellow-400" /> : null}
          </Card>
          <Card interactive onClick={() => handleExport('pdf')} className="flex items-center justify-between py-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <FileText className="w-5 h-5 text-primary" />
              </div>
              <div>
                <span className="font-medium">Export PDF</span>
                <p className="text-xs text-muted">Monthly styled report</p>
              </div>
            </div>
            {isExporting === 'pdf' ? 'Working...' : !isPro ? <Crown className="w-4 h-4 text-yellow-400" /> : null}
          </Card>
        </div>
      </Section>

      <Section title="Danger Zone" icon={AlertTriangle} isOpen={openSections.danger} onToggle={() => toggleSection('danger')}>
        <div className="space-y-3">
          <Card className="space-y-3 border-danger/30">
            <div>
              <h3 className="font-medium text-danger">Leave Space</h3>
              <p className="text-xs text-muted">Leave the current space without deleting it.</p>
            </div>
            <Button variant="danger" onClick={leaveSpace} className="w-full">
              Leave Space
            </Button>
          </Card>

          {isOwner && typedSpace && (
            <Card className="space-y-3 border-danger/30">
              <div>
                <h3 className="font-medium text-danger">Delete Space</h3>
                <p className="text-xs text-muted">Type the space name to archive it for everyone.</p>
              </div>
              <Input
                label="Confirm Space Name"
                value={spaceDeleteConfirm}
                onChange={(event) => setSpaceDeleteConfirm(event.target.value)}
                placeholder={typedSpace.name}
              />
              <Button variant="danger" onClick={deleteSpace} className="w-full">
                Delete Space
              </Button>
            </Card>
          )}

          <Card className="space-y-3 border-danger/30">
            <div>
              <h3 className="font-medium text-danger">Delete Account</h3>
              <p className="text-xs text-muted">Type your email to confirm permanent deletion.</p>
            </div>
            <Input
              label="Confirm Email"
              value={accountDeleteConfirm}
              onChange={(event) => setAccountDeleteConfirm(event.target.value)}
              placeholder={typedUser?.email}
            />
            <Button variant="danger" onClick={deleteAccount} className="w-full">
              Delete Account
            </Button>
          </Card>

          <Card interactive onClick={handleLogout} className="flex items-center gap-3 py-3 text-danger border-danger/30">
            <LogOut className="w-5 h-5" />
            <span className="font-medium">{isLoggingOut ? 'Logging out...' : 'Log Out'}</span>
          </Card>
        </div>
      </Section>

      <BottomSheet isOpen={showThemeSheet} onClose={() => setShowThemeSheet(false)} title="Choose Theme">
        <div className="space-y-2">
          {[
            { value: 'light' as ThemeChoice, icon: Sun, label: 'Light' },
            { value: 'dark' as ThemeChoice, icon: Moon, label: 'Dark' },
            { value: 'system' as ThemeChoice, icon: Palette, label: 'System' },
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => {
                setTheme(option.value);
                setShowThemeSheet(false);
              }}
              className={`w-full p-4 rounded-xl border text-left font-medium flex items-center gap-3 ${
                theme === option.value ? 'bg-primary text-background border-primary' : 'bg-card border-border'
              }`}
            >
              <option.icon className="w-4 h-4" />
              {option.label}
            </button>
          ))}
        </div>
      </BottomSheet>

      <ProUpgradeModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} />
    </div>
  );
}
